/**
 * Help Center Search Index and Algorithm
 *
 * Client-side search functionality for help articles with scoring-based ranking.
 * Search matches are scored based on where they appear:
 * - Title matches: 100 points
 * - Heading matches: 50 points
 * - Content matches: 10 points
 */

export interface HelpArticle {
  slug: string;
  title: string;
  description: string;
  category: 'getting-started' | 'features' | 'guides';
  content: string;
  headings: string[];
}

export interface SearchResult {
  article: HelpArticle;
  score: number;
  matches: {
    inTitle: boolean;
    inHeadings: string[];
    inContent: boolean;
  };
}

/**
 * Pre-indexed help articles for fast searching
 * Content is a simplified version for search purposes (no ASCII diagrams, no code blocks)
 */
export const helpArticles: HelpArticle[] = [
  {
    slug: 'getting-started/quick-start',
    title: 'Quick Start',
    description: 'Get up and running with JobWright in just 5 minutes',
    category: 'getting-started',
    headings: [
      'Complete Your Profile',
      'Upload Your Logo & Branding',
      'Add Your First Client',
      'Create a Quote',
      'Convert to Job',
      'Complete & Invoice',
      'The Workflow at a Glance',
      'Dashboard Overview',
      "What's Next",
      'Tips for Success',
    ],
    content: `Get up and running with JobWright in just 5 minutes! This guide walks you through the essential steps to start managing your trade business. Complete Your Profile. After signing up, you'll land on your dashboard. First, let's set up your business. Click Settings in the sidebar. Go to Organization. Add your Business name, Contact information, Trade type (Plumbing, Electrical, HVAC, etc.). Upload Your Logo & Branding. Make your quotes and invoices look professional. Go to Settings → Branding. Upload your company logo. Choose your brand colors. Preview how it looks on documents. Add Your First Client. Click Clients in the sidebar. Click + New Client. Enter client details: Name (required), Email, Phone, Address. Click Create Client. Create a Quote. Now let's create your first quote. Go to Quotes → + New Quote. Select the client. Add line items: Description, Quantity, Unit price. Set validity date. Click Save as Draft or Send to Client. Convert to Job. When the client approves: Open the quote. Change status to Accepted. Click Convert to Job. The job is created with all quote details! Complete & Invoice. Go to Jobs. Update job status as you work. Mark as Completed when done. Go to Invoices → + New Invoice. Select the job to auto-fill details. Send to client. Your dashboard shows everything at a glance. Use the Workflow Stepper - Click "Get Started" on your dashboard to be guided through each step. Keep client info complete - Email addresses enable quote and invoice delivery. Set quote expiry dates - Creates urgency and keeps your pipeline clean. Update job status regularly - Helps you and your team stay organized. Check reports weekly - Understand what's working in your business.`,
  },
  {
    slug: 'getting-started/dashboard-overview',
    title: 'Dashboard Overview',
    description: 'Learn your way around the JobWright dashboard',
    category: 'getting-started',
    headings: [
      'Dashboard Layout',
      'Quick Stats',
      'Workflow Stepper',
      'Upcoming Jobs',
      'Recent Activity',
      'Quick Actions',
    ],
    content: `Your JobWright dashboard is your command center for managing your trade business. Dashboard Layout. The dashboard is divided into key sections that give you instant visibility into your business operations. Quick Stats. At the top, you'll see real-time metrics: Active jobs count, Quotes sent this month, Revenue month-to-date, Outstanding invoices. These update automatically as you work. Workflow Stepper. The workflow stepper guides you through the complete job lifecycle from client creation to final payment. Click any step to jump directly to that action. Upcoming Jobs. See all scheduled jobs for the next 7 days. Click any job to view details or start work. Color-coded by status for quick identification. Recent Activity. Track the latest changes across your business: Quotes sent, Jobs completed, Invoices paid, Client responses. Quick Actions. The floating action button (FAB) gives you one-click access to create: New Client, New Quote, New Job, New Invoice. This is the fastest way to start work when you're busy on the go.`,
  },
  {
    slug: 'features/clients',
    title: 'Clients',
    description: 'Manage and organize all your customer relationships',
    category: 'features',
    headings: [
      'Overview',
      'Creating a Client',
      'Client List View',
      'Client Detail View',
      'Searching & Filtering',
      'Exporting Clients',
      'Best Practices',
      'Mobile View',
    ],
    content: `Clients are at the heart of your business. JobWright helps you store, organize, and track all your customer relationships in one place. Creating a Client. Navigate to Clients in the sidebar. Click + New Client. Fill in the details: Name (required), Email (used for sending quotes and invoices), Phone (contact phone number), Address (service address or billing address). Click Create Client. Quick Create. Use the floating action button (FAB) or Quick Actions: Click the + button. Select New Client. Fill in details and save. Client List View. View all clients with search, filtering, and sorting capabilities. Search clients by name, email, or phone number. Export to CSV for backups. Client Detail View. Click on any client to see their full profile. View and edit all contact details. Client timeline shows all interactions: Quotes, Jobs, Invoices, Bookings. Quick access to all related items. Searching & Filtering. Type in the search box to find clients by name, email, or phone number. Sort by: Name (A-Z or Z-A), Date created (newest/oldest), Most jobs, Most revenue. Exporting Clients. Click Export CSV on the clients page. The file downloads with columns: Name, Email, Phone, Address, Date Created, Total Jobs, Total Revenue. Best Practices. Complete Information: Always add email for digital communications. Include phone for urgent contact. Add address for service location. Consistent Naming: Use "John Smith" not "john smith". For businesses, include "LLC", "Inc." etc. Avoid abbreviations unless standard. Regular Cleanup: Remove duplicate entries. Update outdated contact info. Archive inactive clients.`,
  },
  {
    slug: 'features/quotes',
    title: 'Quotes',
    description: 'Create professional quotes and estimates for your clients',
    category: 'features',
    headings: [
      'Creating Quotes',
      'Quote Templates',
      'Line Items',
      'Sending Quotes',
      'Quote Status',
      'Converting to Jobs',
    ],
    content: `Create professional, branded quotes that win more business. Creating Quotes. Go to Quotes → + New Quote. Select the client. Add line items with descriptions, quantities, and prices. Set validity date. Save as draft or send to client. Quote Templates. Use pre-built templates for common jobs in your trade: Plumbing templates (drain cleaning, faucet installation, water heater, pipe repair), Electrical templates (panel upgrade, outlet installation, lighting), HVAC templates (AC installation, furnace repair, duct cleaning), Construction templates (foundation repair, drywall, framing, roofing). Line Items. Each line item includes: Description of work, Quantity (hours or units), Unit price, Subtotal calculated automatically. Tax applied based on your settings. Sending Quotes. Click Send Quote. Client receives email with portal link. Quote status changes to SENT. Client can Accept or Reject in portal. Quote Status. Draft: Not yet sent. Sent: Awaiting client response. Accepted: Client approved, ready to convert. Rejected: Client declined. Expired: Past validity date. Converting to Jobs. When quote is accepted, click Convert to Job. Job is created with same client, line items as description, quote total as estimated price. Status set to PENDING.`,
  },
  {
    slug: 'features/jobs',
    title: 'Jobs',
    description: 'Track and manage work from start to finish',
    category: 'features',
    headings: [
      'Creating Jobs',
      'Job Status',
      'Scheduling Jobs',
      'Job Details',
      'Completing Jobs',
      'Job Timeline',
    ],
    content: `Jobs are the work you do. Track every job from creation to completion. Creating Jobs. Create from accepted quote (Convert to Job). Or create directly: Go to Jobs → + New Job. Select client. Enter job details and estimated price. Job Status. Pending: Not yet scheduled. Scheduled: Date and time assigned. In Progress: Work has started. Completed: Work finished. Cancelled: Job was cancelled. Scheduling Jobs. Assign date and time. Select team member(s). Set duration. Add location notes. Job Details. View all job information: Client contact info, Job description and scope, Estimated vs actual costs, Assigned team members, Scheduled date/time, Photos and documentation. Completing Jobs. Update actual costs: Labor hours worked, Materials used, Any change orders. Add photos for documentation. Get customer sign-off. Click Mark Complete. Status changes to COMPLETED. Job Timeline. Track all activities on the job: When created, When scheduled, When work started, When completed, All status changes, Notes and photos added. This creates a complete audit trail for every job.`,
  },
  {
    slug: 'features/invoices',
    title: 'Invoices',
    description: 'Send professional invoices and track payments',
    category: 'features',
    headings: [
      'Creating Invoices',
      'Invoice Details',
      'Sending Invoices',
      'Payment Methods',
      'Payment Tracking',
      'Invoice Status',
    ],
    content: `Get paid faster with professional, branded invoices. Creating Invoices. From completed job: Click Create Invoice. Invoice pre-populated with client info, job line items, final amounts. Or create standalone invoice: Go to Invoices → + New Invoice. Select client. Add line items manually. Invoice Details. Invoice includes: Unique invoice number, Client information, Itemized charges, Subtotal, tax, and total, Payment terms (Net 15, Net 30, etc), Due date. Sending Invoices. Click Send Invoice. Client receives email with: Invoice PDF attached, Link to online payment portal, Payment instructions. Payment Methods. Online payments via Stripe: Credit/debit cards, ACH bank transfer, Apple Pay, Google Pay. Instant notification when paid. Offline payments: Check, Cash, Bank transfer. Record manually when received. Payment Tracking. Track payment status: Unpaid: Not yet paid, Partial: Partially paid, Paid: Fully paid, Overdue: Past due date. Set up automatic payment reminders. Invoice Status. Draft: Not yet sent. Sent: Awaiting payment. Paid: Fully paid. Partial: Partially paid. Overdue: Past due date. Cancelled: Invoice cancelled. View aging reports to track outstanding invoices by days overdue.`,
  },
  {
    slug: 'features/schedule',
    title: 'Schedule',
    description: 'Calendar view of all scheduled jobs and appointments',
    category: 'features',
    headings: [
      'Calendar View',
      'List View',
      'Next Job View',
      'Creating Appointments',
      'Assigning Team',
      'Notifications',
    ],
    content: `Your schedule keeps everyone on the same page about when and where work is happening. Calendar View. Monthly calendar shows all scheduled jobs. Color-coded by status: Pending (gray), Scheduled (blue), In Progress (yellow), Completed (green). Click any date to create new appointment. Click any job to view details. List View. Chronological list of all scheduled jobs. Filter by: Team member, Date range, Status, Client. Sort by date, time, or priority. Search by client name or job description. Next Job View. Shows the next upcoming job for quick reference: Client name and contact, Job description, Scheduled time, Location with map link, Team member assigned. Creating Appointments. Click on calendar date or + New Appointment. Select job or create new. Set date and time. Set duration. Assign team member(s). Add notes. Assigning Team. Assign multiple team members to one job. See each team member's availability. Avoid double-booking. Track who worked which jobs. Notifications. Team members get notifications: When assigned to job, Day before job, Hour before job. Clients can receive: Appointment confirmations, Day-before reminders, When technician is on the way.`,
  },
  {
    slug: 'features/bookings',
    title: 'Bookings',
    description: 'Let clients schedule appointments online 24/7',
    category: 'features',
    headings: [
      'Booking Widget',
      'Setting Availability',
      'Booking Settings',
      'Managing Bookings',
      'Confirmation Emails',
    ],
    content: `Online booking lets clients schedule appointments anytime, even when you're busy or after hours. Booking Widget. Embed on your website or share direct link. Clients can: View your availability, Select service type, Choose date and time, Enter contact info, Add job details. Booking appears instantly on your schedule. Setting Availability. Define your working hours: Days of week, Start and end times, Buffer time between jobs, Blackout dates (holidays, vacation). Set different availability for different services. Booking Settings. Configure booking options: Require advance notice (minimum hours), Maximum booking window (how far ahead), Service duration (15 min, 30 min, 1 hour, etc), Deposit required (optional). Set which services are bookable online. Managing Bookings. All bookings appear in: Schedule calendar, Jobs list (as pending jobs). You can: Confirm, reschedule, or cancel, Add notes, Convert to quote if needed, Assign team member. Confirmation Emails. Clients receive: Immediate booking confirmation, Calendar invite (.ics file), Reminder day before, Link to reschedule or cancel. You receive notification of new booking.`,
  },
  {
    slug: 'features/reports',
    title: 'Reports',
    description: 'Track business performance with detailed analytics',
    category: 'features',
    headings: [
      'Revenue Reports',
      'Job Analytics',
      'Client Reports',
      'Team Performance',
      'Financial Summary',
      'Exporting Reports',
    ],
    content: `Understand your business performance with comprehensive reports and analytics. Revenue Reports. Track income over time: Daily, weekly, monthly, yearly views. Revenue by: Client, Service type, Team member, Job status. Compare periods (this month vs last month). Job Analytics. Total jobs completed, Average job value, Completion rate, Time to completion. Jobs by status breakdown. Quote-to-job conversion rate. Client Reports. Total clients, New clients this period, Repeat customers, Client lifetime value. Top clients by revenue. Inactive clients (no jobs in X days). Team Performance. Jobs completed per team member, Revenue generated per team member, Average job completion time, Customer ratings (if enabled). Financial Summary. Total revenue, Outstanding invoices, Overdue invoices, Average days to payment. Profit margins by job type. Monthly recurring revenue. Exporting Reports. Export any report to: CSV (for Excel), PDF (for sharing). Schedule automatic email delivery: Daily, weekly, monthly. Send to yourself or team members.`,
  },
  {
    slug: 'features/team',
    title: 'Team',
    description: 'Add team members and manage permissions',
    category: 'features',
    headings: [
      'Adding Team Members',
      'User Roles',
      'Permissions',
      'Team Schedule',
      'Activity Tracking',
    ],
    content: `Manage your team and control what each member can access. Adding Team Members. Go to Settings → Team. Click + Add Team Member. Enter: Name, Email, Phone, Role. Send invitation. They receive email to set password. User Roles. Owner: Full access to everything, billing, settings. Admin: Manage all business operations, no billing access. Manager: View reports, manage jobs and schedules. Technician: View assigned jobs, update status, limited access. Limited: Custom permissions. Permissions. Control what each role can do: Create/edit clients, Create/edit quotes, Create/edit jobs, View financials, Manage team, Change settings. Customize permissions per user if needed. Team Schedule. View all team member schedules. See who's assigned to what job. Check availability before scheduling. Avoid conflicts and double-booking. Activity Tracking. See what each team member has done: Jobs completed, Hours logged, Revenue generated, Client interactions. Track productivity and performance.`,
  },
  {
    slug: 'features/client-portal',
    title: 'Client Portal',
    description: 'Give clients secure access to quotes, invoices, and job status',
    category: 'features',
    headings: [
      'Portal Access',
      'What Clients See',
      'Quote Approval',
      'Invoice Payment',
      'Job Status',
      'Communication',
    ],
    content: `The client portal gives your customers 24/7 access to their information. Portal Access. Clients receive portal link via email when you: Send a quote, Send an invoice, Invite them to portal. They can: View without login (magic link), Or create account for full access. What Clients See. Dashboard with: All their quotes, All their jobs, All their invoices, Upcoming appointments. Everything branded with your logo and colors. Quote Approval. Clients can: View quote details, Accept or reject quote, Ask questions, Request changes. You get instant notification of their response. Invoice Payment. Clients can: View invoice details, Pay online via credit card or ACH, Download invoice PDF, View payment history. Payment processed instantly via Stripe. Job Status. Clients can track: Job progress (pending, scheduled, in progress, completed), Scheduled date and time, Assigned technician, Photos and updates you share. Communication. Clients can: Send messages, Upload photos or documents, Schedule follow-up work. All communication tracked in job history.`,
  },
  {
    slug: 'features/settings',
    title: 'Settings',
    description: 'Configure your business preferences and options',
    category: 'features',
    headings: [
      'Organization Settings',
      'Branding',
      'Payment Settings',
      'Email Settings',
      'Tax Settings',
      'Notification Preferences',
    ],
    content: `Customize JobWright to match your business needs. Organization Settings. Business name, Contact information, Trade type, Business address, Phone and email. Tax ID/EIN for invoicing. Branding. Upload company logo, Choose brand colors, Set default font, Preview on documents (quotes, invoices). Make all customer-facing materials match your brand. Payment Settings. Connect Stripe account for online payments. Set default payment terms (Net 15, Net 30, etc). Configure accepted payment methods. Set up ACH bank transfers. Email Settings. Configure email sending: From name (your business name), From address, Reply-to address. Customize email templates for: Quote emails, Invoice emails, Appointment reminders, Payment confirmations. Tax Settings. Set default tax rate. Add multiple tax rates for different jurisdictions. Configure tax exemptions. Choose if tax is included or added. Notification Preferences. Choose which notifications you receive: New quote responses, New bookings, Payment received, Job status changes. Set notification delivery: Email, In-app, or both.`,
  },
  {
    slug: 'guides/complete-workflow',
    title: 'Complete Workflow',
    description: 'End-to-end guide from first contact to final payment',
    category: 'guides',
    headings: [
      'The Complete Job Lifecycle',
      'Create the Client',
      'Create the Quote',
      'Quote Approved - Convert to Job',
      'Schedule the Work',
      'Complete the Work',
      'Create Invoice',
      'Payment Received',
      'The Workflow Wizard',
      'Quick Paths',
      'Best Practices',
      'Common Scenarios',
    ],
    content: `This guide walks you through the entire JobWright workflow from first client contact to final payment. The Complete Job Lifecycle. Client → Quote → Job → Schedule → Complete → Invoice → Paid. Create the Client. When someone contacts you: Go to Clients → New Client. Enter information: Name, Email, Phone, Address. Add notes about how they found you. Or use Quick Actions + button. Create the Quote. After initial assessment: Click Create Quote. Select client. Add line items with descriptions, quantities, prices. Use templates for common jobs. Set validity date. Send to client. Client receives email with portal link. Quote Approved - Convert to Job. When client approves: Open accepted quote. Click Convert to Job. Job created with all quote details. Status set to PENDING. Schedule the Work. Assign date and time. Pick technician. Set duration. Job appears on team calendar. Complete the Work. During job: Update status to IN_PROGRESS. Track time spent. Note materials used. Take photos. When done: Update final costs. Click Mark Complete. Create Invoice. From completed job: Click Create Invoice. Invoice pre-filled with all details. Review and send to client. Client can pay online or offline. Payment Received. Online: Automatic notification. Invoice marked PAID instantly. Offline: Record payment manually. Enter amount, method, date. The Workflow Wizard. For new users: Click Start New Job Workflow on dashboard. Guided step-by-step through entire process. Can skip steps or exit anytime. Progress saved if interrupted. Quick Paths. Emergency repair: Create Job directly, skip quote. Repeat customer: Use existing client record. Small job: Skip quote, go straight to job. Quote only: Create quote, wait for approval. Best Practices. Never lose a lead - create client record immediately. Quote everything over your threshold. Document with photos as you go. Invoice promptly, same day or next day. Follow up on aging invoices weekly. Common Scenarios. Change order: Create supplemental quote, get approval, update job total. Job cancelled: Mark as cancelled, handle refund, note reason. Partial payment: Record amount received, follow up for remainder.`,
  },
  {
    slug: 'guides/mobile-tips',
    title: 'Mobile Tips',
    description: 'Best practices for using JobWright on mobile devices',
    category: 'guides',
    headings: [
      'Mobile Interface',
      'Quick Actions on Mobile',
      'Job Updates',
      'Photo Upload',
      'Offline Mode',
      'Mobile Shortcuts',
    ],
    content: `JobWright is fully responsive and works great on phones and tablets. Mobile Interface. Mobile view automatically adapts: Sidebar becomes hamburger menu, Cards stack vertically, Touch-optimized buttons, Swipe gestures enabled. Quick Actions on Mobile. Floating + button always accessible: Tap to create client, quote, job, invoice. Long press for more options. Job Updates. Update job status on the go: Tap job to open, Swipe to change status, Add photos from camera, Add notes via voice input. Photo Upload. Take photos directly in app: Before/after photos, Damage documentation, Completed work, Materials used. Photos attached to job automatically. Offline Mode. JobWright works offline: View recent jobs and clients, Update job status, Take photos. Changes sync when back online. Mobile Shortcuts. Add to home screen for app-like experience. Use voice input for notes. Enable location for automatic address filling. Quick reply to client messages.`,
  },
  {
    slug: 'guides/keyboard-shortcuts',
    title: 'Keyboard Shortcuts',
    description: 'Speed up your workflow with keyboard shortcuts',
    category: 'guides',
    headings: [
      'Global Shortcuts',
      'Navigation Shortcuts',
      'Action Shortcuts',
      'Form Shortcuts',
    ],
    content: `Power users can work faster with keyboard shortcuts. Global Shortcuts. Press ? to see all shortcuts. Ctrl/Cmd + K: Quick search. Ctrl/Cmd + /: Open command palette. Esc: Close dialog or modal. Navigation Shortcuts. G then D: Go to Dashboard. G then C: Go to Clients. G then Q: Go to Quotes. G then J: Go to Jobs. G then I: Go to Invoices. G then S: Go to Schedule. Action Shortcuts. N then C: New Client. N then Q: New Quote. N then J: New Job. N then I: New Invoice. Ctrl/Cmd + S: Save current form. Ctrl/Cmd + Enter: Save and close. Form Shortcuts. Tab: Next field. Shift + Tab: Previous field. Ctrl/Cmd + S: Save form. Esc: Cancel and close. Use keyboard shortcuts to navigate and create records without touching the mouse.`,
  },
];

/**
 * Search helper function to check if a string contains a query (case-insensitive)
 */
function matchesQuery(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

/**
 * Search through help articles and return ranked results
 *
 * @param query - The search query string
 * @param limit - Maximum number of results to return (default: 10)
 * @returns Array of search results sorted by relevance score
 */
export function searchHelpArticles(query: string, limit: number = 10): SearchResult[] {
  // Return empty array for empty queries
  if (!query || query.trim().length === 0) {
    return [];
  }

  const normalizedQuery = query.trim();
  const results: SearchResult[] = [];

  for (const article of helpArticles) {
    let score = 0;
    const matches = {
      inTitle: false,
      inHeadings: [] as string[],
      inContent: false,
    };

    // Check title match (100 points)
    if (matchesQuery(article.title, normalizedQuery)) {
      score += 100;
      matches.inTitle = true;
    }

    // Check description match (75 points)
    if (matchesQuery(article.description, normalizedQuery)) {
      score += 75;
    }

    // Check heading matches (50 points per heading)
    for (const heading of article.headings) {
      if (matchesQuery(heading, normalizedQuery)) {
        score += 50;
        matches.inHeadings.push(heading);
      }
    }

    // Check content match (10 points)
    if (matchesQuery(article.content, normalizedQuery)) {
      score += 10;
      matches.inContent = true;
    }

    // If there's any match, add to results
    if (score > 0) {
      results.push({
        article,
        score,
        matches,
      });
    }
  }

  // Sort by score (highest first) and limit results
  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Get a preview snippet of content with the query highlighted
 *
 * @param content - The full content text
 * @param query - The search query
 * @param maxLength - Maximum length of the snippet (default: 150)
 * @returns A snippet of text around the first occurrence of the query
 */
export function getContentSnippet(content: string, query: string, maxLength: number = 150): string {
  const normalizedQuery = query.toLowerCase();
  const normalizedContent = content.toLowerCase();
  const index = normalizedContent.indexOf(normalizedQuery);

  if (index === -1) {
    // Query not found, return start of content
    return content.slice(0, maxLength) + (content.length > maxLength ? '...' : '');
  }

  // Find a good starting point (try to start at a sentence or word boundary)
  const start = Math.max(0, index - 60);
  const end = Math.min(content.length, index + query.length + 90);

  let snippet = content.slice(start, end);

  // Add ellipsis if we're not at the start/end
  if (start > 0) {
    snippet = '...' + snippet;
  }
  if (end < content.length) {
    snippet = snippet + '...';
  }

  return snippet;
}
